import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Button } from "react-bootstrap";
import { format } from "date-fns";
import PhoneNumberFormat from "../PhoneNumberFormate/PhoneNumberFormate";

export default function OrganizationRequestsTable({ fetchApi, linkTo }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const auth = useSelector((state) => state.auth);
  const [allRequests, setAllRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!auth.token) {
        setError("Please log in to view organization requests.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchApi(auth.token);
        if (response?.success) {
          setAllRequests(response.data || []);
        } else {
          throw new Error(response?.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error(
          error?.message || "Failed to fetch data. Please try again.",
          {
            position: "top-center",
          }
        );
        setError(error?.message || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [auth.token, fetchApi]);

  const totalPages = Math.ceil((allRequests?.length || 0) / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRequests = Array.isArray(allRequests)
    ? allRequests.slice(startIndex, endIndex)
    : [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_review: { label: "Pending Review", class: "status-pending" },
    };

    const config = statusConfig[status?.toLowerCase()] || {
      label: status || "Unknown",
      class: "status-default",
    };

    return (
      <span className={`status-badge ${config.class}`}>{config.label}</span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "/";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime());
  };

  return (
    <>
      <style jsx>{`
        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .detail-item {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #f05203;
        }

        .detail-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-label {
          font-size: 0.875rem;
          font-weight: 700;
          color: #1f1f1f;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1f2937;
          line-height: 1.5;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-default {
          background: #f3f4f6;
          color: #6b7280;
        }

        .message-box {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          font-size: 1rem;
          line-height: 1.7;
          min-height: 80px;
        }

        .detail-value a {
          color: #4299e1;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s ease;
        }

        .detail-value a:hover {
          color: #3182ce;
        }

        .detail-value a svg {
          margin-left: 8px;
          vertical-align: middle;
        }

        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      {isLoading && (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      )}
      <div className="d-flex align-item-center justify-content-end d-sm-none">
        <label className="me-2 flex-shrink-0 align-self-center">
          No of Rows
        </label>
        <select
          className="form-control ms-16"
          value={rowsPerPage}
          onChange={handleRowsChange}
          style={{ width: "100px" }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="table-responsive mb-10">
        <table className="table student-table custom-table align-middle">
          <thead className="position-sticky top-0">
            <tr>
              <th className="ah-text-base" width="10%">
                Sr No.
              </th>
              <th className="ah-text-base" width="20%">
                Organization Name
              </th>
              <th className="ah-text-base" width="20%">
                Contact Person
              </th>
              <th className="ah-text-base" width="20%">
                Email
              </th>
              <th className="ah-text-base" width="20%">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5">Loading...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5">
                  <div className="alert alert-danger">Error: {error}</div>
                </td>
              </tr>
            ) : currentRequests.length > 0 ? (
              currentRequests.map((request, index) => (
                <tr key={request.id}>
                  <td>
                    <div className="counter-block">
                      {startIndex + index + 1}
                    </div>
                  </td>
                  <td>
                    <strong className="ah-text-sm fw-semibold">
                      {request.organization_name}
                    </strong>
                  </td>
                  <td className="fw-medium">{request.name}</td>
                  <td className="fw-medium">{request.email}</td>
                  <td className="fw-medium">
                    <Button
                      variant="primary"
                      className="fw-semibold no-shadow py-7 ah-text-sm"
                      onClick={() => handleViewDetails(request)}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">
                  <div className="alert alert-info">
                    No organization requests found.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-center justify-content-sm-between align-items-center custom-pagination-holder">
        <div className="mx-auto">
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg
              style={{ transform: "rotate(-90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              className={`bg-transparent border-0 ${
                currentPage === i + 1
                  ? "pagination-item active"
                  : "pagination-item"
              } me-2`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <svg
              style={{ transform: "rotate(90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="align-item-center d-none d-sm-flex">
          <label className="me-2 flex-shrink-0 align-self-center">
            No of Rows
          </label>
          <select
            className="form-control ms-16"
            value={rowsPerPage}
            onChange={handleRowsChange}
            style={{ width: "100px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Modal for viewing request details */}
      <div
        className={`modal ${showModal ? "show" : ""}`}
        style={{
          display: showModal ? "block" : "none",
          background: "rgba(0,0,0,0.5)",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseModal();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Organization Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {selectedRequest && (
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Organization Name</div>
                    <div className="detail-value">
                      {selectedRequest.organization_name}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Industry Type</div>
                    <div className="detail-value">
                      {selectedRequest.industry_type}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Number of Students</div>
                    <div className="detail-value">
                      {selectedRequest.number_of_students}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Website</div>
                    <div className="detail-value">
                      <a
                        href={selectedRequest.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <svg
                          className="me-2 ms-0"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                        </svg>
                        {selectedRequest.website_url}
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Contact Person</div>
                    <div className="detail-value">{selectedRequest.name}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Designation</div>
                    <div className="detail-value">
                      {selectedRequest.designation}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Email Address</div>
                    <div className="detail-value">
                      <a
                        href={`mailto:${selectedRequest.email}`}
                        className="text-decoration-none  d-flex align-items-center gap-1"
                      >
                        <svg
                          className="me-2 ms-0"
                          margi
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                        </svg>
                        {selectedRequest.email}
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Contact Number</div>
                    <div className="detail-value">
                      <a
                        href={`tel:${selectedRequest.contact_number}`}
                        className="text-decoration-none d-flex align-items-center gap-1"
                      >
                        <svg
                          className="me-2 ms-0"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z" />
                        </svg>
                        <PhoneNumberFormat
                          number={selectedRequest.contact_number}
                          type="national"
                        />
                      </a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Address</div>
                    <div className="detail-value">
                      {selectedRequest.location}
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label">Submitted On</div>
                    <div className="detail-value">
                      {formatDate(selectedRequest?.created_at)}
                    </div>
                  </div>
                  <div className="detail-item full-width">
                    <div className="detail-label">Message</div>
                    <div className="detail-value">
                      {selectedRequest.message}
                    </div>
                  </div>
                  
                  {/* <div className="detail-item">
                    <div className="detail-label">Status</div>
                    <div className="detail-value">{getStatusBadge('pending_review')}</div>
                  </div> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
